import React, { useEffect, useState } from 'react';
import { Todo, BucketTheme } from '../types';
import { Pause, CheckCircle2, Hourglass, Minimize2 } from 'lucide-react';
import { useLanguage } from '../i18n';

interface ActiveTaskOverlayProps {
  todo: Todo;
  bucketName: string;
  bucketTheme: BucketTheme;
  onStop: () => void;
  onComplete: () => void;
}

const THEME_STYLES: Record<BucketTheme, { border: string; glow: string; text: string; bg: string }> = {
  [BucketTheme.Blue]: { border: 'border-blue-500', glow: 'shadow-blue-500/20', text: 'text-blue-400', bg: 'bg-blue-950/90' },
  [BucketTheme.Purple]: { border: 'border-purple-500', glow: 'shadow-purple-500/20', text: 'text-purple-400', bg: 'bg-purple-950/90' },
  [BucketTheme.Green]: { border: 'border-emerald-500', glow: 'shadow-emerald-500/20', text: 'text-emerald-400', bg: 'bg-emerald-950/90' },
  [BucketTheme.Orange]: { border: 'border-orange-500', glow: 'shadow-orange-500/20', text: 'text-orange-400', bg: 'bg-orange-950/90' },
  [BucketTheme.Pink]: { border: 'border-pink-500', glow: 'shadow-pink-500/20', text: 'text-pink-400', bg: 'bg-pink-950/90' },
  [BucketTheme.Red]: { border: 'border-red-500', glow: 'shadow-red-500/20', text: 'text-red-400', bg: 'bg-red-950/90' },
  [BucketTheme.Teal]: { border: 'border-teal-500', glow: 'shadow-teal-500/20', text: 'text-teal-400', bg: 'bg-teal-950/90' },
  [BucketTheme.Yellow]: { border: 'border-yellow-500', glow: 'shadow-yellow-500/20', text: 'text-yellow-400', bg: 'bg-yellow-950/90' },
  [BucketTheme.Indigo]: { border: 'border-indigo-500', glow: 'shadow-indigo-500/20', text: 'text-indigo-400', bg: 'bg-indigo-950/90' },
  [BucketTheme.Cyan]: { border: 'border-cyan-500', glow: 'shadow-cyan-500/20', text: 'text-cyan-400', bg: 'bg-cyan-950/90' },
  [BucketTheme.Lime]: { border: 'border-lime-500', glow: 'shadow-lime-500/20', text: 'text-lime-400', bg: 'bg-lime-950/90' },
  [BucketTheme.Rose]: { border: 'border-rose-500', glow: 'shadow-rose-500/20', text: 'text-rose-400', bg: 'bg-rose-950/90' },
};

const formatDuration = (ms: number) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor((ms / (1000 * 60 * 60)));

    const p = (n: number) => n.toString().padStart(2, '0');
    
    if (hours > 0) return `${hours}:${p(minutes)}:${p(seconds)}`;
    return `${p(minutes)}:${p(seconds)}`;
};

export const ActiveTaskOverlay: React.FC<ActiveTaskOverlayProps> = ({ todo, bucketName, bucketTheme, onStop, onComplete }) => {
  const { t } = useLanguage();
  const [currentDuration, setCurrentDuration] = useState(0);
  const styles = THEME_STYLES[bucketTheme];

  useEffect(() => {
    const updateTime = () => {
        if (todo.activeSessionStart) {
            const sessionTime = Date.now() - todo.activeSessionStart;
            setCurrentDuration((todo.timeSpent || 0) + sessionTime);
        } else {
            setCurrentDuration(todo.timeSpent || 0);
        }
    };

    updateTime(); // Initial update
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [todo.activeSessionStart, todo.timeSpent]);

  return (
    <div className="fixed bottom-0 left-0 right-0 flex justify-center z-[90] pointer-events-none px-4" style={{ paddingBottom: 'calc(1.5rem + var(--safe-area-inset-bottom))' }}>
      <div 
        className={`pointer-events-auto w-full max-w-lg ${styles.bg} border-t-4 ${styles.border} shadow-2xl ${styles.glow} backdrop-blur-md rounded-2xl overflow-hidden animate-in slide-in-from-bottom-20 duration-500`}
      >
        <div className="p-4 flex items-center justify-between gap-4">
            
            {/* Left: Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 ${styles.text}`}>
                        {bucketName}
                    </span>
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider animate-pulse flex items-center gap-1">
                        <Hourglass size={10} className="animate-spin-slow" />
                        {t.workingOn}
                    </span>
                </div>
                <h4 className="text-white font-medium truncate text-lg">{todo.text}</h4>
            </div>

            {/* Middle: Timer */}
            <div className="flex flex-col items-end">
                 <span className="text-[10px] text-slate-500 font-medium uppercase">{t.sessionTime}</span>
                 <span className="text-2xl font-mono font-bold text-white tabular-nums tracking-tight">
                    {formatDuration(currentDuration)}
                 </span>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
                <button 
                    onClick={onStop}
                    className="h-10 w-10 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all active:scale-95"
                    title={t.stopTimer}
                >
                    <Pause size={20} className="fill-current" />
                </button>
                <button 
                    onClick={onComplete}
                    className={`h-10 px-4 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${styles.bg.replace('bg-', 'from-').replace('/90','')} to-white/20 hover:brightness-110 text-white font-medium transition-all active:scale-95 border border-white/10`}
                    title={t.finish}
                >
                    <CheckCircle2 size={18} />
                    <span className="hidden sm:inline">{t.finish}</span>
                </button>
            </div>
        </div>
        
        {/* Progress Bar Animation */}
        <div className="h-0.5 w-full bg-white/5 overflow-hidden">
             <div className={`h-full w-full ${styles.bg.replace('bg-', 'bg-text-')} animate-progress-indeterminate opacity-50`}></div>
        </div>
      </div>
    </div>
  );
};